import React from "react";
import { Query } from "@apollo/client/react/components";
import { QueryResult } from "@apollo/client";
import { BetQLSportItem } from "src/common-services";

import TeamLogo from "../../Team/TeamLogo";
import { TEAM_SUMMARY_QUERY } from "../../../gql/queries";
import styles from "./styles.scss";

import moment, { Moment } from "moment-timezone";
import { rfc3986EncodeURIComponent } from "src/services/UtilService";

const DATE_LOOKUP_FORMAT: string = "M/D/YYYY";

// The exception to these rules is the section of rows meant
// only for sports with `hasPitchers` flagged true (i.e. MLB).
// All leagues include a coach/manager row.
const leagueShowsRow = {
  ppg: ["NBA", "CBK", "CFB", "NFL"],
  htPpg: ["NBA", "CBK", "CFB", "NFL"],
  oppPpg: ["NFL"],
  tennisProps: ["ATP", "WTA"],
};

function setTextStyle(
  teamStat: number | null,
  oppStat: number | null,
  isLowerBetter?: boolean | undefined
): { [rule: string]: string } | {} {
  if (teamStat == null || oppStat == null) {
    // null | undefined
    return {};
  } else {
    const hasAdvantage = !isLowerBetter ? teamStat > oppStat : teamStat < oppStat;
    return {
      color: hasAdvantage ? "#02a16c" : "#FF4F4D",
      fontWeight: "bold",
      fontSize: "15px",
    };
  }
}

function calculateAge(birthday: Moment) {
  return moment().diff(birthday, "years");
}

function getFormColors(form: string | null) {
  if (!form) {
    return "--";
  }
  let array: any = form.split("");
  return array.map((letter: string, index: number) => {
    switch (letter) {
      case "W":
        return (
          <span key={index} className="team-summary-item__form--green">
            W
          </span>
        );
      case "L":
        return (
          <span key={index} className="team-summary-item__form--red">
            L
          </span>
        );
      case "D":
        return (
          <span key={index} className="team-summary-item__form">
            D
          </span>
        );
      default:
        return "";
    }
  });
}

function TeamSummaryItem(props: {
  league: any;
  team: any;
  opponent: any;
  currentSportObj: BetQLSportItem;
  classNames: string[];
}) {
  const { league, team, opponent, currentSportObj, classNames } = props;
  const { hasPitchers, hasGoals } = currentSportObj;

  const teamStats = team.teamStats || {};
  const oppStats = opponent.teamStats || {};

  // soccer stats only display if hasGoals is true
  let teamGoalsPerGame: any = null;
  let teamShotsPerGame: any = null;
  let teamShotsOnGoalPerGame: any = null;
  // opponent stats are just for logic, so they don't need display fallbacks
  let oppGoalsPerGame: any = null;
  let oppShotsPerGame: any = null;
  let oppShotsOnGoalPerGame: any = null;

  if (hasGoals) {
    const { games: teamGames, goals: teamGoals, shots: teamShots, shotsOnGoal: teamShotsOnGoal } = teamStats;
    if (teamGames !== null && teamGames > 0) {
      teamGoalsPerGame = teamGoals !== null ? teamGoals / teamGames : null;

      teamShotsPerGame = teamShots !== null ? teamShots / teamGames : null;

      teamShotsOnGoalPerGame = teamShotsOnGoal !== null ? teamShotsOnGoal / teamGames : null;
    }
    const { games: oppGames, goals: oppGoals, shots: oppShots, shotsOnGoal: oppShotsOnGoal } = oppStats;
    if (oppGames !== null && oppGames > 0) {
      oppGoalsPerGame = oppGoals !== null ? oppGoals / oppGames : null;
      oppShotsPerGame = oppShots !== null ? oppShots / oppGames : null;
      oppShotsOnGoalPerGame = oppShotsOnGoal !== null ? oppShotsOnGoal / oppGames : null;
    }
  }

  let tennisStats, dob, highRankDate, age;
  if (currentSportObj.isTennis) {
    tennisStats = JSON.parse(teamStats.tennisStats);
    dob = moment.utc(tennisStats.DOB);
    highRankDate = moment.utc(tennisStats.Date_of_Hi_Rank);
    age = calculateAge(dob);
  }

  return (
    <div className={`team-summary-item ${classNames.join(" ")}`}>
      <div className="team-summary-item__header">
        <TeamLogo
          leagueName={league.name}
          teamAbbreviation={currentSportObj.isTennis ? team.country : team.preferredAbbreviation}
          size={40}
          containerClassName="team-summary-item__logo"
        />
        <div className="team-summary-item__title">{team.fullName} Summary</div>
      </div>
      {/* (Almost) All teams display a coach/manager row. Hide this on desktop for soccer since no coachName */}
      <div className={currentSportObj.isSoccer || currentSportObj.isTennis ? "team-summary-item__mobile-header" : ""}>
        <TeamLogo
          leagueName={league.name}
          teamAbbreviation={currentSportObj.isTennis ? team.country : team.preferredAbbreviation}
          size={40}
          containerClassName="team-summary-item__logo--mobile"
        />
        <div
          className="d-md-none"
          style={{
            fontWeight: "bold",
            textAlign: "center",
            ...(currentSportObj.isSoccer && currentSportObj.isTennis ? {} : { marginBottom: 10 }),
          }}
        >
          {team.preferredAbbreviation}
        </div>
        {!currentSportObj.isSoccer && !currentSportObj.isTennis ? (
          <React.Fragment>
            <div>{league.name === "MLB" ? "Manager" : "Head Coach"}</div>
            <div style={teamStats.coachName ? { fontWeight: "bold", fontSize: 15 } : {}}>
              {teamStats.coachName || "--"}
            </div>
          </React.Fragment>
        ) : null}
      </div>
      {/* Rows to show tennis player bio */}
      {leagueShowsRow.tennisProps.includes(league.name) && (
        <React.Fragment>
          <div>
            <div>Rank</div>
            <div style={setTextStyle(teamStats.rank, oppStats.rank)}>{teamStats.rank || "--"}</div>
          </div>
          <div>
            <div>Age</div>
            <div style={{ fontWeight: "bold" }}>{age}</div>
          </div>
          <div>
            <div>Gender</div>
            <div style={{ fontWeight: "bold" }}>{parseInt(tennisStats.Gender, 10) === 0 ? "Male" : "Female"}</div>
          </div>
          <div>
            <div>Country</div>
            <div style={{ fontWeight: "bold" }}>{tennisStats.country.Country_Full_Name}</div>
          </div>
          <div>
            <div>Height</div>
            <div style={{ fontWeight: "bold" }}>
              {tennisStats.Height_Feet ? tennisStats.Height_Feet + "'" + tennisStats.Height_inches : "--"}
            </div>
          </div>
          <div>
            <div>Handedness</div>
            <div style={{ fontWeight: "bold" }}>{tennisStats.Plays ? tennisStats.Plays : "--"}</div>
          </div>
          <div>
            <div>Highest Rank</div>
            <div>
              <span style={setTextStyle(parseInt(teamStats.Hi_Rank, 10), parseInt(oppStats.Hi_Rank, 10))}>
                {tennisStats.Hi_Rank}
              </span>{" "}
              on {highRankDate?.format(DATE_LOOKUP_FORMAT)}
            </div>
          </div>
        </React.Fragment>
      )}
      {/* Rows to show are determined from `leagueShowsRow` configs */}
      {leagueShowsRow.ppg.includes(league.name) && (
        <div>
          <div>Avg. Pts. Scored</div>
          <div style={setTextStyle(teamStats.ppg, oppStats.ppg)}>{teamStats.ppg || "--"}</div>
        </div>
      )}
      {leagueShowsRow.htPpg.includes(league.name) && (
        <div>
          <div>1H Pts. Scored</div>
          <div style={setTextStyle(teamStats.htPpg, oppStats.htPpg)}>{teamStats.htPpg || "--"}</div>
        </div>
      )}
      {leagueShowsRow.oppPpg.includes(league.name) && (
        <div>
          <div>Def. Pts. Allowed</div>
          {/* reversed stats because higher is worse */}
          <div style={setTextStyle(oppStats.oppPpg, teamStats.oppPpg)}>{teamStats.oppPpg || "--"}</div>
        </div>
      )}
      {/* Below rows are only relevant to leagues with pitchers (i.e. MLB) */}
      {hasPitchers && (
        <>
          <div>
            <div>Runs Per Game</div>
            <div style={setTextStyle(teamStats.teamRunsGame, oppStats.teamRunsGame)}>
              {teamStats.teamRunsGame || "--"}
            </div>
          </div>
          <div>
            <div>Opp Runs Per Game</div>
            {/* reversed stats because higher is worse */}
            <div style={setTextStyle(oppStats.oppRunsGame, teamStats.oppRunsGame)}>{teamStats.oppRunsGame || "--"}</div>
          </div>
          <div>
            <div>Home Runs For</div>
            <div style={setTextStyle(teamStats.teamHr, oppStats.teamHr)}>{teamStats.teamHr || "--"}</div>
          </div>
          <div>
            <div>Home Runs Allowed</div>
            <div style={setTextStyle(teamStats.oppHr, oppStats.oppHr)}>{teamStats.oppHr || "--"}</div>
          </div>
        </>
      )}
      {hasGoals ? (
        <>
          <div>
            <div>Recent Form</div>
            <div>{getFormColors(teamStats.form)}</div>
          </div>
          <div>
            <div>Shots per Game</div>
            <div style={setTextStyle(teamShotsPerGame, oppShotsPerGame)}>
              {(teamShotsPerGame && teamShotsPerGame.toFixed(2)) || "--"}
            </div>
          </div>
          <div>
            <div>Avg. Shots on Goal</div>
            <div style={setTextStyle(teamShotsOnGoalPerGame, oppShotsOnGoalPerGame)}>
              {(teamShotsOnGoalPerGame && teamShotsOnGoalPerGame.toFixed(2)) || "--"}
            </div>
          </div>
          <div>
            <div>Avg. Goals Scored</div>
            <div style={setTextStyle(teamGoalsPerGame, oppGoalsPerGame)}>
              {(teamGoalsPerGame && teamGoalsPerGame.toFixed(2)) || "--"}
            </div>
          </div>
          <div>
            <div>Avg. Goals Allowed</div>
            <div style={setTextStyle(teamStats.oppPpg, oppStats.oppPpg, true)}>{teamStats.oppPpg || "--"}</div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function TeamSummary(props: { slugId: string; currentSportObj: BetQLSportItem }) {
  const { slugId, currentSportObj } = props;

  return (
    <Query
      ssr={false}
      query={TEAM_SUMMARY_QUERY}
      variables={{
        slugId: rfc3986EncodeURIComponent(decodeURIComponent(slugId as string)),
        hasPitchers: currentSportObj.hasPitchers,
        hasGoals: currentSportObj.hasGoals,
      }}
    >
      {({ data }: QueryResult) => {
        if (!data || !data || !data.event) {
          return null;
        }
        const { league, homeTeam, awayTeam } = data.event;
        return (
          <React.Fragment>
            <div id="team-summary" className="team-summary__outer-container">
              <div className="team-summary__container">
                <div className="team-summary__header">Team Summary</div>
                <div className="team-summary__items">
                  <TeamSummaryItem
                    league={league}
                    team={awayTeam}
                    opponent={homeTeam}
                    currentSportObj={currentSportObj}
                    classNames={["team-summary-item--left"]}
                  />
                  <TeamSummaryItem
                    league={league}
                    team={homeTeam}
                    opponent={awayTeam}
                    currentSportObj={currentSportObj}
                    classNames={["team-summary-item--right"]}
                  />
                </div>
              </div>
              <style jsx global>
                {styles}
              </style>
            </div>
          </React.Fragment>
        );
      }}
    </Query>
  );
}

export default TeamSummary;
